import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	Heading,
	Text,
	Image,
	FormErrorMessage,
	RadioGroup,
	Input,
	Switch,
} from 'src/components/ui';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import LocationModal from './LocationModal';
import ServiceAreaModal from './ServiceAreaModal';
import LocationCard from '../common/LocationCard';
import GeocodedLocationCard from '../common/GeocodedLocationCard';

import {
	StringInputHook,
	FileInput,
	SelectSearchInputHook,
	PhoneInput,
} from '../common/form';
import FusionLoading from '../common/FusionLoading';
import AlertMessage from '../common/AlertMessage';
import HexColorPicker from '../common/HexColorPicker';
import UserContext from 'src/contexts/user/UserContext';
import {
	CompanyAccountSchema,
	ICompanyAccountForm,
	ILocation,
	IColorPreference,
	IPaletteItem,
} from 'src/lib/schemas/account/company';
import {
	getCompanyAccount,
	updateCompanyAccount,
	uploadFontFile,
	getServiceableAreas,
	createServiceableArea,
	updateServiceableArea,
	deleteServiceableArea,
	getLocations,
	createLocation,
	updateLocation,
	deleteLocation,
	ICreateLocationPayload,
} from 'src/services/account';
import useAccountConfig from 'src/hooks/useAccountConfig';
import useToggleWithPayload from 'src/hooks/useToggleWithPayload';
import { toastSuccess } from 'src/services/toast';
import { IOption } from 'src/lib/schemas/misc';
import MultiLevelDropdown from '../common/MultiLevelDropdown';
import { IIndustry, getIndustries } from 'src/services/industries';
import { omit } from 'lodash';
import { validateWebsiteUrl } from 'src/lib/utils';
import { uploadFile } from 'src/services/fileUpload';
import { FileRejection } from 'react-dropzone';
import TemplateContext from 'src/contexts/templates/TemplatesContext';
import countries from 'src/lib/constants/countries.json';
import CustomModal from '../common/CustomModal';
import AddLocationCard from '../common/AddLocationCard';

const COUNTRY_OPTIONS = countries
	.map((c: { code: string; name: string }) => ({
		value: c.code,
		label: c.name,
	}))
	.sort((a, b) => {
		if (a.value === 'US') return -1;
		if (b.value === 'US') return 1;
		return a.label.localeCompare(b.label);
	});
const getStateOptions = (countryCode: string) => {
	const country = countries.find(
		(c: { code: string }) => c.code === countryCode,
	);
	return country && country.states
		? country.states.map((s: { code: string; name: string }) => ({
				value: s.code,
				label: s.name,
			}))
		: [];
};

const IMAGE_NOT_VALID_MESSAGE = 'Invalid image';

/** Human-readable labels for palette references */
const REFERENCE_LABELS: Record<string, string> = {
	'logo-primary': 'Primary',
	'logo-secondary': 'Secondary',
	'logo-tertiary': 'Tertiary',
	'logo-quaternary': 'Quaternary',
	'logo-light': 'Light',
	'logo-dark': 'Dark',
	'logo-neutral': 'Neutral',
};

interface BrandColorsSectionProps {
	colorPreferences?: IColorPreference[];
	onChange: (prefs: IColorPreference[]) => void;
}

const BrandColorsSection = ({ colorPreferences, onChange }: BrandColorsSectionProps) => {
	const currentPalette: IPaletteItem[] =
		colorPreferences?.[0]?.palette ?? [];

	// Local state for hex text inputs so users can type freely
	const [hexInputs, setHexInputs] = useState<Record<number, string>>({});

	/** Update a color value in the palette */
	const updateColor = (index: number, hex: string) => {
		const updated = currentPalette.map((p, i) =>
			i === index ? { ...p, value: hex } : p,
		);
		const pref: IColorPreference = {
			name: colorPreferences?.[0]?.name || 'Brand Colors',
			source: 'manual',
			palette: updated,
		};
		onChange([pref]);
	};

	/** Handle hex input change — always update text, apply color when valid */
	const handleHexInput = (index: number, value: string) => {
		setHexInputs(prev => ({ ...prev, [index]: value }));
		const cleaned = value.startsWith('#') ? value : `#${value}`;
		if (/^#[0-9a-fA-F]{6}$/.test(cleaned)) {
			updateColor(index, cleaned.toLowerCase());
		}
	};

	/** On blur, reset local input to the actual saved value */
	const handleHexBlur = (index: number) => {
		setHexInputs(prev => {
			const next = { ...prev };
			delete next[index];
			return next;
		});
	};

	if (currentPalette.length === 0) return null;

	return (
		<Flex direction="column" gap={4}>
			<Heading as="h3" size="md">
				Brand Colors
			</Heading>
			<Text fontSize="sm" color="gray.500">
				Extracted from your logo. Click to edit.
			</Text>

			<Flex direction="column" gap={3}>
				{currentPalette.map((item: any, idx) => {
				if (item.reference === 'neutralize_to') return null;
				if (item.reference && item.reference !== 'logo-primary' && item.reference !== 'logo-secondary') return null;
				return (
					<Flex key={idx} align="center" gap={3}>
						<HexColorPicker
							value={item.value || '#333333'}
							onChange={(hex: string) => updateColor(idx, hex)}
						/>
						<Flex direction="column" flex={1}>
							<Text fontSize="sm" fontWeight="medium">
								{REFERENCE_LABELS[item.reference] || item.reference}
							</Text>
						</Flex>
						<Text fontFamily="mono" fontSize="xs" color="gray.500">
							{item.value}
						</Text>
					</Flex>
				);
				})}
			</Flex>
		</Flex>
	);
};

interface Location {
	name: string;
	address: {
		street: string;
		city: string;
		state: string;
		postalCode: string;
		country: string;
		lat: string | number;
		long: string | number;
	};
	isDefault: boolean;
	phone: string;
}

interface LocationForApi {
	name: string;
	address: {
		street: string;
		city: string;
		state: string;
		postalCode: string;
		country: string;
		lat: number;
		long: number;
	};
	isDefault: boolean;
	phone: string;
}

interface LocationWithNumberCoordinates {
	name: string;
	address: {
		street: string;
		city: string;
		state: string;
		postalCode: string;
		country: string;
		lat: number;
		long: number;
	};
	isDefault: boolean;
	phone: string;
}

type AddressWithFormatted = {
	street: string;
	city: string;
	state: string;
	postalCode: string;
	country: string;
	lat: string | number;
	long: string | number;
	formatted?: string;
	placeId?: string;
	regionCode?: string;
};

interface CompanyAccountFormProps {
	highlightFields?: string[];
}

const CompanyAccountForm = ({ highlightFields = [] }: CompanyAccountFormProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { user, account, setAccount } = useContext(UserContext);
	const { config, isLoading: isConfigLoading } = useAccountConfig();
	const [industries, setIndustries] = useState<IIndustry[] | null>(null);

	// Track fields that have been filled (to remove highlight)
	const [filledFields, setFilledFields] = useState<Set<string>>(new Set());
	
	const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);
	
	const [showNavigator, setShowNavigator] = useState(highlightFields.length > 0);

	// Check if a field should be highlighted (only if in highlightFields AND not yet filled)
	const shouldHighlight = (fieldName: string) => 
		highlightFields.includes(fieldName) && !filledFields.has(fieldName);

	const remainingHighlightFields = useMemo(() => 
		highlightFields.filter(field => !filledFields.has(field)),
		[highlightFields, filledFields]
	);

	const phoneFieldRef = useRef<HTMLDivElement>(null);
	const industryFieldRef = useRef<HTMLDivElement>(null);
	const nameFieldRef = useRef<HTMLDivElement>(null);

	const fieldRefs: Record<string, React.RefObject<HTMLDivElement>> = {
		phone: phoneFieldRef,
		industry: industryFieldRef,
		name: nameFieldRef,
	};

	const navigateToField = (fieldName: string) => {
		const ref = fieldRefs[fieldName];
		if (ref?.current) {
			ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	};

	const handleNavigateHighlight = (direction: 'prev' | 'next') => {
		if (highlightFields.length === 0) return;
		
		let newIndex = currentHighlightIndex;
		if (direction === 'next') {
			newIndex = (currentHighlightIndex + 1) % highlightFields.length;
		} else {
			newIndex = currentHighlightIndex === 0 
				? highlightFields.length - 1 
				: currentHighlightIndex - 1;
		}
		setCurrentHighlightIndex(newIndex);
		navigateToField(highlightFields[newIndex]);
	};

	const closeNavigator = () => {
		setShowNavigator(false);
		setFilledFields(new Set(highlightFields)); // Mark all as filled to remove highlights
		window.history.replaceState({}, '', '/account/company');
	};

	// Scroll to first highlighted field on mount
	useEffect(() => {
		if (highlightFields.length > 0) {
			setShowNavigator(true);
			// Small delay to ensure DOM is ready
			const timer = setTimeout(() => {
				navigateToField(highlightFields[0]);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [highlightFields]);

	// Auto-hide navigator when all fields are filled
	useEffect(() => {
		if (remainingHighlightFields.length === 0 && highlightFields.length > 0) {
			// All fields filled, hide navigator after a short delay
			const timer = setTimeout(() => {
				setShowNavigator(false);
				window.history.replaceState({}, '', '/account/company');
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [remainingHighlightFields.length, highlightFields.length]);

	const [loadingIndustries, setLoadingIndustries] = useState(true);
	const [isLogoLoading, setIsLogoLoading] = useState(false);
	const [removeBackground, setRemoveBackground] = useState(false);
	const [customOptions, setCustomOptions] = useState<IOption[]>([]);
	const settingsModalToggle = useToggleWithPayload();
	const navigate = useNavigate();
	const { setSelectedIndustry, fetchTemplates } = useContext(TemplateContext);
	const [defaultLocationIdx, setDefaultLocationIdx] = useState<number | null>(
		null,
	);
	const [isLocationModalOpen, setLocationModalOpen] = useState(false);
	const [editingLocationIdx, setEditingLocationIdx] = useState<number | null>(
		null,
	);
	const [editingLocationId, setEditingLocationId] = useState<string | null>(
		null,
	);
	const [locationDraft, setLocationDraft] = useState<any>(null);
	const [isSavingLocation, setIsSavingLocation] = useState(false);
	const [accountLocations, setAccountLocations] = useState<any[]>([]);
	const [isLoadingLocations, setIsLoadingLocations] = useState(false);
	const [locationsError, setLocationsError] = useState<string | null>(null);
	const [serviceableAreas, setServiceableAreas] = useState<any[]>([]);
	const [isLoadingServiceableAreas, setIsLoadingServiceableAreas] =
		useState(false);
	const [serviceableAreasError, setServiceableAreasError] = useState<
		string | null
	>(null);
	const [isServiceableAreaModalOpen, setServiceableAreaModalOpen] =
		useState(false);
	const [serviceableAreaDraft, setServiceableAreaDraft] = useState<any>({
		name: '',
		address: {
			street: '',
			city: '',
			state: '',
			postalCode: '',
			country: '',
			lat: '',
			long: '',
		},
		radius: '45',
	});
	const [editingServiceAreaIdx, setEditingServiceAreaIdx] = useState<
		number | null
	>(null);
	const [editingServiceAreaId, setEditingServiceAreaId] = useState<
		string | null
	>(null);
	const [isSavingServiceArea, setIsSavingServiceArea] = useState(false);
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
		useState(false);
	const [serviceAreaToDelete, setServiceAreaToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isDeletingServiceArea, setIsDeletingServiceArea] = useState(false);
	const [
		isConfirmDeleteLocationModalOpen,
		setIsConfirmDeleteLocationModalOpen,
	] = useState(false);
	const [locationToDelete, setLocationToDelete] = useState<{
		idx: number;
		name: string;
		id: string;
	} | null>(null);
	const [isDeletingLocation, setIsDeletingLocation] = useState(false);
	const [isSubmittingForm, setIsSubmittingForm] = useState(false);

	const formMethods = useForm<ICompanyAccountForm>({
		resolver: zodResolver(CompanyAccountSchema),
	});
	const {
		reset,
		formState,
		watch,
		setValue,
		clearErrors,
		setFocus,
		setError: setFieldError,
	} = formMethods;

	const { isDirty } = formState;

	useEffect(() => {
		const elements = document.querySelectorAll('.chakra-container');
		elements.forEach((element) => {
			(element as HTMLElement).style.minWidth = '100%';
		});
	}, []);

	const fetchIndustries = async () => {
		setLoadingIndustries(true);
		const response = await getIndustries();
		response && setIndustries(response);
		setLoadingIndustries(false);
	};

	useEffect(() => {
		fetchIndustries();
	}, []);

	const fetchCompanyAccount = async () => {
		setIsLoading(true);
		try {
			if (!user) {
				setIsLoading(false);
				return;
			}

			const data = await getCompanyAccount(user.account);
			const { timezone, language, brandIdentity, ...rest } = data;
			const defaultTimezone =
				timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
			const defaultLanguage = language ?? 'English';

			const brandIdentityInfo = brandIdentity
				? { brandIdentity: { fontType: brandIdentity.fontType } }
				: {};

			reset({
				...rest,
				account: user.account,
				timezone: defaultTimezone,
				language: defaultLanguage,
				...brandIdentityInfo,
			});
		} catch (error) {
			reset({
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				language: 'English',
			});
			setError('Failed to fetch company account');
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (!user || isLoading) return;

		fetchCompanyAccount();
		reset({ account: user.account });
	}, [user]);

	const industry = watch('industry');
	const websiteLink = watch('websiteLink');
	const logo = watch('logo');
	const logoSquareLight = watch('logoSquareLight');
	const logoSquareDark = watch('logoSquareDark');
	const favicon = watch('favicon');
	const logoDark = watch('logoDark');
	const fontType = watch('brandIdentity.fontType');
	const phone = watch('phone');
	const logoHasError = formState.errors['logo'];

	// Update filledFields when form values change (to remove highlight)
	useEffect(() => {
		if (highlightFields.length === 0) return;
		
		const newFilledFields = new Set(filledFields);
		
		if (phone && phone.trim().length > 0) {
			newFilledFields.add('phone');
		}
		if (industry && industry.name) {
			newFilledFields.add('industry');
		}
		
		if (newFilledFields.size !== filledFields.size) {
			setFilledFields(newFilledFields);
		}
	}, [phone, industry, highlightFields]);
	const memoizedLogoError = useMemo(
		() => logoHasError?.message,
		[logoHasError],
	);

	useEffect(() => {
		if (memoizedLogoError !== undefined) {
			setFieldError('logo', { message: memoizedLogoError });
		}
	}, [memoizedLogoError, setFieldError]);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const handleFontUpload = async (
		fileOrString: File | string,
		fileRejections?: FileRejection[],
	) => {
		if (fileRejections && fileRejections.length > 0) {
			console.error('File rejected', fileRejections);
			onImageError('File type not accepted');
			return;
		}

		if (typeof fileOrString === 'string') {
			return;
		}

		try {
			const { message, newFontFamilies } = await uploadFontFile(fileOrString);
			setValue('brandIdentity.fontFamilies', newFontFamilies, { shouldDirty: true });
			if (message) {
				setSuccessMessage(message);
				toastSuccess(message);
			}
			clearErrors('brandIdentity.fontFamilies');
		} catch (error) {
			console.error('Error uploading fontFile', error);
			onImageError('Error uploading fontFile');
			setError('Error uploading fontFile');
		}
	};

	const loadCustomFont = async () => {
		if (fontType) {
			const customFont = new FontFace('CustomFont', `url(${fontType})`);
			await customFont.load();
			document.fonts.add(customFont);
			const family = customFont.family;
			const isFontExists = config?.fonts.find((f) => f.value === fontType);
			if (!isFontExists) {
				setCustomOptions([{ value: fontType, label: family }]);
			} else {
				setCustomOptions([]);
			}
			document.fonts.add(customFont);
		}
	};

	useEffect(() => {
		loadCustomFont();
	}, [fontType]);

	const handleWebsiteLinkChange = (e: any, updateValue = false) => {
		const url = e.target.value;
		const { isValid, updatedUrl } = validateWebsiteUrl(url);
		if (!isValid) {
			setFieldError('websiteLink', { message: 'Please enter a valid URL' });
			return;
		}
		if (updateValue) {
			setValue('websiteLink', updatedUrl);
		}
		clearErrors('websiteLink');
	};

	const handleFontChange = async (
		input: File | string,
		fileRejections?: FileRejection[],
	) => {
		if (fileRejections && fileRejections.length > 0) {
			console.error('File rejected', fileRejections);
			onImageError('File type not accepted');
			return;
		}
		const newFont = typeof input === 'string' ? input : (input as any).path;
		setValue('brandIdentity.fontType', newFont, { shouldDirty: true });
	};

	const handleLogoChange = async (
		input: File | string,
		fileRejections?: FileRejection[],
	) => {
		const minWidth = 100;
		const minHeight = 30;

		if (fileRejections && fileRejections.length > 0) {
			console.error('File rejected:', fileRejections);
			onImageError('File type not accepted');
			return;
		}

		// Handle URL input (send to upload endpoint for processing)
		if (typeof input === 'string') {
			if (input.trim()) {
				// Validate the URL format
				try {
					new URL(input);
				} catch (error) {
					console.error('❌ Invalid URL format:', input);
					onImageError('Please enter a valid URL');
					return;
				}
			} else {
				setValue('logo', '');
				return;
			}
		}

		// Handle upload (both file and URL)
		const isUrl = typeof input === 'string';
		setIsLogoLoading(true);
		try {
			const {
				imageUrl,
				logoSquareUrl,
				logoLandscapeUrl,
				logoSquareLightUrl,
				logoSquareDarkUrl,
				logoLandscapeLightUrl,
				logoLandscapeDarkUrl,
				faviconUrl,
				logoDarkUrl,
				palette,
			} = await uploadFile(
				input,
				true,
				minWidth,
				minHeight,
				180, // minShortestSide - minimum 180px as requested
				undefined,
				removeBackground,
				false,
				true,
			);
			setIsLogoLoading(false);
			clearErrors('logo');
			setValue('logo', imageUrl, { shouldDirty: true });
			if (logoSquareUrl) setValue('logoSquare', logoSquareUrl, { shouldDirty: true });
			if (logoLandscapeUrl) setValue('logoLandscape', logoLandscapeUrl, { shouldDirty: true });
			setValue('logoSquareLight', logoSquareLightUrl || '', { shouldDirty: true });
			setValue('logoSquareDark', logoSquareDarkUrl || '', { shouldDirty: true });
			setValue('logoLandscapeLight', logoLandscapeLightUrl || '', { shouldDirty: true });
			setValue('logoLandscapeDark', logoLandscapeDarkUrl || '', { shouldDirty: true });
			if (faviconUrl) setValue('favicon', faviconUrl, { shouldDirty: true });
			setValue('logoDark', logoDarkUrl || '', { shouldDirty: true });

			// Replace colorPreferences with extracted palette from new logo
			if (palette?.length > 0) {
				setValue('colorPreferences', [{
					name: 'Logo Colors',
					source: 'logo',
					palette,
				}], { shouldDirty: true });
			} else {
				// B&W logo or no colors extracted — clear existing colorPreferences
				setValue('colorPreferences', [], { shouldDirty: true });
			}
		} catch (error: any) {
			console.error('❌ Logo upload failed:', error);
			onImageError(error.response?.data?.message || IMAGE_NOT_VALID_MESSAGE);
			setIsLogoLoading(false);
		}
	};

	const handleLogoUrlChange = (url: string) => {
		setValue('logo', url, { shouldDirty: true });
	};

	const handleTypographyChange = (url: string) => {
		setValue('brandIdentity.fontType', url, { shouldDirty: true });
	};

	const handleIndustryChange = (industry: IIndustry) => {
		setValue('industry', omit(industry, 'children', 'parentId', '_id'), { shouldDirty: true });
	};

	const onImageError = (message?: string) => {
		setFieldError('logo', { message });
		console.error('eror', message);
	};

	const handleConfirmModal = () => {
		navigate('/projects/campaigns/new');
	};

	const validateRequiredFields = () => {
		const formData = formMethods.getValues();
		const requiredErrors: string[] = [];

		if (!formData.name?.trim()) requiredErrors.push('Name is required');
		if (!formData.websiteLink?.trim())
			requiredErrors.push('Website is required');
		if (!formData.phone?.trim())
			requiredErrors.push('Phone number is required');
		if (!formData.logo?.trim()) requiredErrors.push('Logo is required');
		if (!formData.account?.trim()) requiredErrors.push('Account is required');

		return requiredErrors;
	};

	const onSubmit = async (e: any) => {
		e.preventDefault();

		if (!user) return;

		setIsSubmittingForm(true);

		const validationErrors = validateRequiredFields();
		if (validationErrors.length > 0) {
			setError(validationErrors.join(', '));
			setIsSubmittingForm(false);
			return;
		}

		const formData = formMethods.getValues();
		const allowedFields = [
			'account',
			'name',
			'appStoreLink',
			'playStoreLink',
			'websiteLink',
			'logo',
			'logoSquare',
			'logoLandscape',
			'logoSquareLight',
			'logoSquareDark',
			'logoLandscapeLight',
			'logoLandscapeDark',
			'favicon',
			'logoDark',
			'timezone',
			'language',
			'brandIdentity',
			'description',
			'keywords',
			'phone',
			'colorPreferences',
		];

		const updatedFormData: any = {};
		allowedFields.forEach((field) => {
			if ((formData as any)[field] !== undefined) {
				updatedFormData[field] = (formData as any)[field];
			}
		});

		if (industry) {
			const updatedIndustry = omit(industry, '_id', 'children');
			updatedFormData.industry = updatedIndustry;
		}
		if (logoHasError) {
			setFocus('logo');
			setIsSubmittingForm(false);
			return;
		}

		try {
			setError(null);
			const data = await updateCompanyAccount(updatedFormData);
			toastSuccess('Your changes have been saved');
			setSelectedIndustry(industry);
			if (industry) {
				await fetchTemplates(industry);
			}
			const isValidAccount = Boolean(account?.name);
			if (!isValidAccount) {
				settingsModalToggle.onOpen();
				setTimeout(() => {
					settingsModalToggle.onClose();
				}, 10000);
			}
			setAccount(data);
			// Reset form with current values to clear isDirty state
			reset(formMethods.getValues());
			setIsSubmittingForm(false);
		} catch (error: any) {
			const { message } = error.response.data;
			setError(message);
			setIsSubmittingForm(false);
		}
	};

	const isDataLoading = isLoading || isConfigLoading;

	useEffect(() => {
		const idx = accountLocations.findIndex((loc) => loc.isDefault);
		setDefaultLocationIdx(idx >= 0 ? idx : null);
	}, [accountLocations]);

	const formatAddress = (address: any) =>
		[
			address.street,
			address.city,
			address.state,
			address.postalCode,
			address.country,
		]
			.filter(Boolean)
			.join(', ');

	const openAddLocationModal = () => {
		setEditingLocationIdx(null);
		setEditingLocationId(null);
		resetLocationDraft();
		setLocationModalOpen(true);
	};
	const openEditLocationModal = (idx: number) => {
		const loc = accountLocations[idx];
		setEditingLocationIdx(idx);
		setEditingLocationId(loc._id || loc.id);
		const formatted = formatAddress(loc.address || {});
		setLocationDraft({
			...loc,
			address: {
				...(loc.address as AddressWithFormatted),
				lat: loc.address?.lat?.toString() || '40.7128',
				long: loc.address?.long?.toString() || '-74.0060',
				formatted: formatted,
			},
		});
		setLocationModalOpen(true);
	};
	const closeServiceableAreaModal = () => {
		setServiceableAreaModalOpen(false);
		setEditingServiceAreaIdx(null);
		setEditingServiceAreaId(null);
		resetServiceAreaDraft();
	};
	const resetServiceAreaDraft = () => {
		setServiceableAreaDraft({
			name: '',
			address: {
				street: '',
				city: '',
				state: '',
				postalCode: '',
				country: '',
				lat: '',
				long: '',
				placeId: '',
			},
			radius: '45',
		});
	};
	const openAddServiceAreaModal = () => {
		setEditingServiceAreaIdx(null);
		setEditingServiceAreaId(null);
		resetServiceAreaDraft();
		setServiceableAreaModalOpen(true);
	};
	const openEditServiceAreaModal = (idx: number) => {
		const area = serviceableAreas[idx];
		setEditingServiceAreaIdx(idx);
		setEditingServiceAreaId(area.id);

		if (area.type === 'geographic') {
			// Geographic type service area
			setServiceableAreaDraft({
				type: 'geographic',
				name: area.name,
				geographic: area.geographic,
			});
		} else {
			// Radius type service area (default)
			// Format the address for display
			const formattedAddress = [
				area.address?.street,
				area.address?.city,
				area.address?.state,
				area.address?.postalCode,
				area.address?.country,
			]
				.filter(Boolean)
				.join(', ');

			setServiceableAreaDraft({
				type: 'radius',
				name: area.name,
				address: {
					street: area.address?.street,
					city: area.address?.city,
					state: area.address?.state,
					postalCode: area.address?.postalCode,
					country: area.address?.country,
					lat: area.address?.lat?.toString() || '',
					long: area.address?.long?.toString() || '',
					formatted: formattedAddress,
				},
				radius: area.radius?.toString() || '',
			});
		}
		setServiceableAreaModalOpen(true);
	};
	const handleRemoveServiceArea = async (id: string) => {
		const area = serviceableAreas.find((area) => area.id === id);
		if (area) {
			setServiceAreaToDelete({ id: area.id, name: area.name });
			setIsConfirmDeleteModalOpen(true);
		}
	};
	const confirmDeleteServiceArea = async () => {
		if (!serviceAreaToDelete) return;

		setIsDeletingServiceArea(true);
		try {
			await deleteServiceableArea(serviceAreaToDelete.id);
			// Refresh list from server to ensure consistency
			const areas = await getServiceableAreas();
			setServiceableAreas(areas);
			toastSuccess('Service area removed');
			setIsConfirmDeleteModalOpen(false);
			setServiceAreaToDelete(null);
		} catch (err: any) {
			setServiceableAreasError(err.message || 'Failed to remove service area');
		} finally {
			setIsDeletingServiceArea(false);
		}
	};
	const handleRemoveLocation = (idx: number) => {
		const location = accountLocations[idx];
		const locationName = location?.name || `Location ${idx + 1}`;
		const locationId = location?._id || location?.id;
		setLocationToDelete({ idx, name: locationName, id: locationId });
		setIsConfirmDeleteLocationModalOpen(true);
	};
	const confirmDeleteLocation = async () => {
		if (locationToDelete === null || !locationToDelete.id) return;

		setIsDeletingLocation(true);
		try {
			// Delete via API
			await deleteLocation(locationToDelete.id);

			// Update local state only after API success
			setAccountLocations((prev) =>
				prev.filter((loc) => (loc._id || loc.id) !== locationToDelete.id)
			);

			toastSuccess('Location removed');
			setIsConfirmDeleteLocationModalOpen(false);
			setLocationToDelete(null);
		} catch (err: any) {
			setError(err?.response?.data?.message || 'Failed to remove location');
		} finally {
			setIsDeletingLocation(false);
		}
	};
	const closeLocationModal = () => {
		setLocationModalOpen(false);
		setEditingLocationIdx(null);
		setEditingLocationId(null);
		resetLocationDraft();
	};
	const handleSaveLocation = async (locationData: any) => {
		setIsSavingLocation(true);
		try {
			const payload: ICreateLocationPayload = {
				name: locationData.name,
				address: {
					street: locationData.address.street || '',
					city: locationData.address.city || '',
					state: locationData.address.state || '',
					postalCode: locationData.address.postalCode || '',
					country: locationData.address.country || '',
					lat: parseFloat(locationData.address.lat) || 0,
					long: parseFloat(locationData.address.long) || 0,
					placeId: locationData.address.placeId,
					regionCode: locationData.address.regionCode,
				},
				isDefault: locationData.isDefault || false,
				phone: locationData.phone || '',
			};

			if (editingLocationId) {
				// Update existing location
				const updatedLocation = await updateLocation(editingLocationId, payload);
				
				// Update local state
				setAccountLocations((prev) =>
					prev.map((loc) =>
						(loc._id || loc.id) === editingLocationId ? updatedLocation : loc
					)
				);
				
				toastSuccess('Location updated');
			} else {
				// Create new location
				const newLocation = await createLocation(payload);
				
				// Add to local state
				setAccountLocations((prev) => [...prev, newLocation]);
				
				toastSuccess('Location created');
			}

			closeLocationModal();
		} catch (err: any) {
			setError(err?.response?.data?.message || 'Failed to save location');
		} finally {
			setIsSavingLocation(false);
		}
	};

	// Fetch locations on mount
	useEffect(() => {
		const fetchAccountLocations = async () => {
			setIsLoadingLocations(true);
			setLocationsError(null);
			try {
				const locs = await getLocations();
				setAccountLocations(locs);
			} catch (err: any) {
				setLocationsError(err.message || 'Failed to fetch locations');
			} finally {
				setIsLoadingLocations(false);
			}
		};
		fetchAccountLocations();
	}, []);

	// Fetch service areas on mount
	useEffect(() => {
		const fetchServiceableAreas = async () => {
			setIsLoadingServiceableAreas(true);
			setServiceableAreasError(null);
			try {
				const areas = await getServiceableAreas();
				setServiceableAreas(areas);
			} catch (err: any) {
				setServiceableAreasError(
					err.message || 'Failed to fetch service areas',
				);
			} finally {
				setIsLoadingServiceableAreas(false);
			}
		};
		fetchServiceableAreas();
	}, []);

	const handleSaveServiceableArea = async (serviceAreaData: any) => {
		setIsSavingServiceArea(true);
		try {
			let payload: any;

			if (serviceAreaData.type === 'geographic') {
				// Geographic entity service area
				payload = {
					type: 'geographic',
					name: serviceAreaData.name,
					geographic: serviceAreaData.geographic,
				};
			} else {
				// Radius-based service area (default)
				payload = {
					type: 'radius',
					name: serviceAreaData.name,
					address: {
						...serviceAreaData.address,
						lat: parseFloat(serviceAreaData.address.lat),
						long: parseFloat(serviceAreaData.address.long),
					},
					radius: parseFloat(serviceAreaData.radius),
				};
			}

			if (editingServiceAreaId) {
				// Update existing service area
				await updateServiceableArea(editingServiceAreaId, payload);
				toastSuccess('Service area updated');
			} else {
				// Create new service area
				await createServiceableArea(payload);
				toastSuccess('Service area created');
			}

			// Only update local state after API success
			setServiceableAreaModalOpen(false);

			// Refresh list from server to ensure consistency
			const areas = await getServiceableAreas();
			setServiceableAreas(areas);
		} catch (err: any) {
			setServiceableAreasError(err.message || 'Failed to save service area');
			throw err; // Re-throw so the modal can handle the error
		} finally {
			setIsSavingServiceArea(false);
		}
	};

	const resetLocationDraft = () => {
		const emptyAddress: AddressWithFormatted = {
			street: '',
			city: '',
			state: '',
			postalCode: '',
			country: '',
			lat: '',
			long: '',
			formatted: '',
			regionCode: '',
		};
		setLocationDraft({
			name: '',
			address: { ...emptyAddress, formatted: formatAddress(emptyAddress) },
			isDefault: false,
			phone: '',
		});
	};

	return (
		<>
			<FusionLoading isLoading={isDataLoading} />
			{!isDataLoading && (
				<>
					<Heading>Company Info</Heading>
					{error && (
						<AlertMessage status="error" my={4}>
							{error}
						</AlertMessage>
					)}
					<FormProvider {...formMethods}>
						<form onSubmit={onSubmit}>
							<Flex direction="column" gap="20px" mt={5} maxW="full">
								<Flex direction="column" gap="20px" maxW="full">
									<Box ref={nameFieldRef}>
										<StringInputHook
											name="name"
											label="Name"
											placeholder="Name"
											required
										/>
									</Box>
									<StringInputHook
										name="websiteLink"
										label="Website"
										placeholder="Website"
										required
										formControlProps={{
											onBlur: (e) => handleWebsiteLinkChange(e, true),
											onChange: handleWebsiteLinkChange,
										}}
									/>
								<Box
									ref={phoneFieldRef}
									borderWidth={shouldHighlight('phone') ? '2px' : '0'}
									borderColor="orange.400"
									borderRadius="md"
									p={shouldHighlight('phone') ? 3 : 0}
									bg={shouldHighlight('phone') ? 'orange.50' : 'transparent'}
									transition="all 0.3s"
								>
									<PhoneInput
										name="phone"
										label="Phone Number"
										placeholder="e.g. +1-555-123-4567"
										required
									/>
								</Box>
									<Box>
										<Flex alignItems="center" justifyContent="space-between">
											<Heading as="h3" size="md">
												Locations
											</Heading>
										</Flex>
										<Flex direction="column" gap={4} mt={2}>
											{accountLocations.map((location, idx) => (
												<LocationCard
													key={location._id || location.id || idx}
													type="location"
													title={location.name?.trim() || `Location ${idx + 1}`}
													address={{
														street: location.address?.street,
														city: location.address?.city,
														state: location.address?.state,
														postalCode: location.address?.postalCode,
														country: location.address?.country,
														lat: location.address?.lat,
														long: location.address?.long,
													}}
													onEdit={() => openEditLocationModal(idx)}
													onRemove={() => handleRemoveLocation(idx)}
													height="300px"
													maxWidth="500px"
												/>
											))}
											<AddLocationCard
												type="location"
												onClick={openAddLocationModal}
											/>
										</Flex>
									</Box>
									<LocationModal
										isOpen={isLocationModalOpen}
										onClose={closeLocationModal}
										onSave={handleSaveLocation}
										locationDraft={locationDraft}
										isSavingLocation={isSavingLocation}
										editingLocationIdx={editingLocationIdx}
									/>
									{/* Service Areas Section */}
									<Box mt={8}>
										<Flex alignItems="center" justifyContent="space-between">
											<Heading as="h3" size="md">
												Service Areas
											</Heading>
										</Flex>
										<Flex direction="column" gap={4} mt={2}>
											{serviceableAreas.map((area, idx) => {
												return (
													<GeocodedLocationCard
														key={area.id || idx}
														type="service-area"
														title={area.name}
														serviceAreaType={area.type}
														address={area.address || undefined}
														geographic={area.geographic || undefined}
														radius={area.radius || undefined}
														onEdit={() => openEditServiceAreaModal(idx)}
														onRemove={() => handleRemoveServiceArea(area.id)}
														height="300px"
														maxWidth="500px"
													/>
												);
											})}
											<AddLocationCard
												type="service-area"
												onClick={openAddServiceAreaModal}
											/>
										</Flex>
									</Box>
									<Flex direction="column" gap={5}>
										<FormControl isInvalid={Boolean(logoHasError)}>
											<StringInputHook
												name="logo"
												label="Logo"
												placeholder="Enter Logo URL"
												required
												withErrorMessage={false}
												errorMessage={
													formState.errors['logo']?.message as string
												}
												inputProps={{
													onChange: (e) => {
														const url = e.target.value;
														handleLogoChange(url);
													},
												}}
											/>
											<FormErrorMessage>
												{formState.errors['logo']?.message}
											</FormErrorMessage>
											<Flex justify="center" py={2} alignItems="center" gap={2}>
												<Box flex={1} h="1px" bg="gray.100" />
												<Text>OR</Text>
												<Box flex={1} h="1px" bg="gray.100" />
												<Box />
											</Flex>
											<Flex alignItems="center" gap={4}>
											<FileInput
												name="logo"
												acceptImages
												uploadButtonText={
													logo ? 'Change image' : 'Upload image'
												}
												onUrlChange={handleLogoUrlChange}
												uploadPath={`${user?.account}/logo`}
												onDrop={(acceptedFile, fileRejections) =>
													handleLogoChange(acceptedFile, fileRejections)
												}
												onLoading={setIsLogoLoading}
											/>
											<Flex alignItems="center" gap={2}>
												<Switch
													size="sm"
													isChecked={removeBackground}
													onChange={(e: any) => setRemoveBackground(e.target.checked)}
												/>
												<Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
													Remove background
												</Text>
											</Flex>
										</Flex>
										</FormControl>
										<FusionLoading isLoading={isLogoLoading} />
										{(logoSquareLight || logoSquareDark) &&
											!logoHasError &&
											!isLogoLoading && (
												<Flex gap={4} alignItems="flex-start">
													{logoSquareLight && (
														<Box>
															<Text
																fontSize="sm"
																color="gray.600"
																mb={2}
																fontWeight="medium"
															>
																Light Logo
															</Text>
															<Box
																maxW="100px"
																border="1px solid"
																borderColor="gray.200"
																borderRadius="md"
																p={2}
																bg="black"
																display="flex"
																alignItems="center"
																justifyContent="center"
																h="100px"
																w="100px"
															>
																<Image
																	src={logoSquareLight}
																	alt="Logo for light theme"
																	maxW="100%"
																	maxH="100%"
																	objectFit="contain"
																	onError={() => onImageError()}
																/>
															</Box>
														</Box>
													)}
													{logoSquareDark && (
														<Box>
															<Text
																fontSize="sm"
																color="gray.600"
																mb={2}
																fontWeight="medium"
															>
																Dark Logo
															</Text>
															<Box
																maxW="100px"
																border="1px solid"
																borderColor="gray.200"
																borderRadius="md"
																p={2}
																bg="white"
																display="flex"
																alignItems="center"
																justifyContent="center"
																h="100px"
																w="100px"
															>
																<Image
																	src={logoSquareDark}
																	alt="Logo for dark theme"
																	maxW="100%"
																	maxH="100%"
																	objectFit="contain"
																	onError={() => onImageError()}
																/>
															</Box>
														</Box>
													)}
												</Flex>
											)}
										{/* Fallback to original logo if no light/dark variations */}
										{!logoSquareLight &&
											!logoSquareDark &&
											logo &&
											!logoHasError &&
											!isLogoLoading && (
												<Image
													src={logo}
													alt="Account logo"
													maxW="100px"
													onError={() => onImageError()}
												/>
											)}
										{/* Favicon shown independently */}
										{favicon &&
											!logoHasError &&
											!isLogoLoading && (
												<Box>
													<Text
														fontSize="sm"
														color="gray.600"
														mb={2}
														fontWeight="medium"
													>
														Website Icon
													</Text>
													<Box
														maxW="48px"
														border="1px solid"
														borderColor="gray.200"
														borderRadius="md"
														p={1}
														bg="white"
														display="flex"
														alignItems="center"
														justifyContent="center"
														h="48px"
														w="48px"
													>
														<Image
															src={favicon}
															alt="Favicon"
															maxW="100%"
															maxH="100%"
															objectFit="contain"
															onError={() => onImageError()}
														/>
													</Box>
												</Box>
											)}
									</Flex>
									{/* Brand Colors Section */}
									<BrandColorsSection
										colorPreferences={watch('colorPreferences')}
										onChange={(prefs) => setValue('colorPreferences', prefs, { shouldDirty: true })}
									/>
									<Divider />
									<StringInputHook
										name="appStoreLink"
										label="App Store Link"
										placeholder="App Store Link"
									/>
									<StringInputHook
										name="playStoreLink"
										label="Play Store Link"
										placeholder="Play Store Link"
									/>
								{industries && (
									<Box
										ref={industryFieldRef}
										borderWidth={shouldHighlight('industry') ? '2px' : '0'}
										borderColor="orange.400"
										borderRadius="md"
										p={shouldHighlight('industry') ? 3 : 0}
										bg={shouldHighlight('industry') ? 'orange.50' : 'transparent'}
										transition="all 0.3s"
									>
										<MultiLevelDropdown
											onSelect={handleIndustryChange}
											items={industries}
											initialValue={industry}
											label="Industry"
											isLoading={loadingIndustries}
										/>
									</Box>
								)}
									<Flex direction="column" gap={5}>
										<FormControl>
											<SelectSearchInputHook
												name="brandIdentity.fontType"
												label="Typography"
												placeholder="Select"
												options={
													!isConfigLoading && config?.fonts ? config.fonts : []
												}
											/>
											<Flex justify="center" py={2} alignItems="center" gap={2}>
												<Box flex={1} h="1px" bg="gray.100" />
												<Text>OR</Text>
												<Box flex={1} h="1px" bg="gray.100" />
												<Box />
											</Flex>
											<FileInput
												name="brandIdentity.fontFamilies"
												acceptFonts
												uploadButtonText="Upload font"
												onDrop={(acceptedFile, fileRejections) =>
													handleFontUpload(acceptedFile, fileRejections)
												}
											/>
										</FormControl>
										{fontType && (
											<Text fontFamily="CustomFont" fontSize={18}>
												ABCDEFGHIJKLMNOPQRSTUVWXYZ
												<br />
												abcdefghijklmnopqrstuvwxyz
											</Text>
										)}
									</Flex>
								</Flex>
							</Flex>
							<Divider my={5} mt={8} />
							<Accordion allowMultiple>
								<AccordionItem border="0">
									<AccordionButton padding="0" _hover={{ padding: '0' }}>
										<Box as="span" flex="1" textAlign="left">
											<Heading>Advanced</Heading>
										</Box>
										<AccordionIcon />
									</AccordionButton>
									<AccordionPanel padding="16px 0">
										<Flex gap="32px">
											<SelectSearchInputHook
												name="timezone"
												label="Timezone"
												placeholder="Select"
												options={
													!isConfigLoading && config?.timezones
														? config.timezones
														: []
												}
											/>
										</Flex>
									</AccordionPanel>
								</AccordionItem>
							</Accordion>
							<Divider my={5} />
							{/* Spacer to prevent content from being hidden behind sticky bar */}
							<Box height="120px" />
						</form>

						{/* Sticky bottom bar - Warning message + Navigator + Save Changes */}
						<Box
							position="sticky"
							bottom="0"
							left="0"
							right="0"
							bg="white"
							borderTop="1px solid"
							borderColor="gray.200"
							py={3}
							ml={-6}
							mr={-6}
							px={6}
							zIndex={100}
							boxShadow="0 -4px 12px rgba(0, 0, 0, 0.1)"
						>
							{/* Warning row with navigator - only show if there are remaining highlight fields */}
							{showNavigator && remainingHighlightFields.length > 0 && (
								<Flex
									align="center"
									justify="space-between"
									bg="orange.50"
									border="1px solid"
									borderColor="orange.200"
									borderRadius="md"
									px={4}
									py={2}
									mb={3}
								>
									<Flex align="center" gap={2}>
										<Box color="orange.500" fontSize="md">⚠️</Box>
										<Text color="orange.800" fontSize="sm">
											Please complete the highlighted fields below to enable AI Voice Agent.
										</Text>
									</Flex>
									{/* Chrome-style field navigator */}
									<Flex
										align="center"
										bg="white"
										border="1px solid"
										borderColor="gray.300"
										borderRadius="sm"
										px={2}
										py={1}
										gap={1}
									>
										<Text fontSize="sm" color="gray.600" px={1}>
											{currentHighlightIndex + 1}/{highlightFields.length}
										</Text>
										<Flex align="center" gap={0}>
											<Box
												as="button"
												type="button"
												p={1}
												color="gray.500"
												_hover={{ color: 'gray.700' }}
												onClick={() => handleNavigateHighlight('prev')}
												cursor="pointer"
											>
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
													<polyline points="18 15 12 9 6 15"></polyline>
												</svg>
											</Box>
											<Box
												as="button"
												type="button"
												p={1}
												color="gray.500"
												_hover={{ color: 'gray.700' }}
												onClick={() => handleNavigateHighlight('next')}
												cursor="pointer"
											>
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
													<polyline points="6 9 12 15 18 9"></polyline>
												</svg>
											</Box>
										</Flex>
										<Box
											as="button"
											type="button"
											p={1}
											color="gray.500"
											_hover={{ color: 'gray.700' }}
											onClick={closeNavigator}
											cursor="pointer"
										>
											<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<line x1="18" y1="6" x2="6" y2="18"></line>
												<line x1="6" y1="6" x2="18" y2="18"></line>
											</svg>
										</Box>
									</Flex>
								</Flex>
							)}
							{/* Save Changes row */}
							<Flex alignItems="center" justifyContent="flex-end" gap={4}>
								{isDirty && !isSubmittingForm && (
									<Flex alignItems="center" gap={2}>
										<Box
											w="8px"
											h="8px"
											borderRadius="full"
											bg="orange.400"
										/>
										<Text fontSize="sm" color="gray.600">
											You have unsaved changes
										</Text>
									</Flex>
								)}
								<Button
									variant="orangeSolid"
									type="submit"
									onClick={onSubmit}
									isLoading={isSubmittingForm || isLogoLoading}
									loadingText={isSubmittingForm ? 'Submitting...' : 'Loading...'}
								>
									Save Changes
								</Button>
							</Flex>
						</Box>

						{/* Service Area Modal */}
						<ServiceAreaModal
							isOpen={isServiceableAreaModalOpen}
							onClose={closeServiceableAreaModal}
							onSave={handleSaveServiceableArea}
							serviceAreaDraft={serviceableAreaDraft}
							editingServiceAreaIdx={editingServiceAreaIdx}
							isSaving={isSavingServiceArea}
						/>

						{/* Delete Confirmation Modal */}
						<CustomModal
							isOpen={isConfirmDeleteModalOpen}
							onClose={() => {
								setIsConfirmDeleteModalOpen(false);
								setServiceAreaToDelete(null);
							}}
							header="Confirm Deletion"
							footer={
								<Flex w="full" justifyContent="flex-end" gap={2}>
									<Button
										onClick={() => {
											setIsConfirmDeleteModalOpen(false);
											setServiceAreaToDelete(null);
										}}
										isDisabled={isDeletingServiceArea}
									>
										Cancel
									</Button>
									<Button
										onClick={confirmDeleteServiceArea}
										colorScheme="secondary"
										isLoading={isDeletingServiceArea}
										loadingText="Deleting..."
									>
										Delete
									</Button>
								</Flex>
							}
							size="md"
							stickyFooter
						>
							<Flex direction="column" gap={4} p={2}>
								<Text>
									Are you sure you want to delete the service area{' '}
									<Text as="span" fontWeight="bold">
										&quot;{serviceAreaToDelete?.name}&quot;
									</Text>
									?
								</Text>
							</Flex>
						</CustomModal>

						{/* Delete Location Confirmation Modal */}
						<CustomModal
							isOpen={isConfirmDeleteLocationModalOpen}
							onClose={() => {
								setIsConfirmDeleteLocationModalOpen(false);
								setLocationToDelete(null);
							}}
							header="Confirm Deletion"
							footer={
								<Flex w="full" justifyContent="flex-end" gap={2}>
									<Button
										onClick={() => {
											setIsConfirmDeleteLocationModalOpen(false);
											setLocationToDelete(null);
										}}
										isDisabled={isDeletingLocation}
									>
										Cancel
									</Button>
									<Button
										onClick={confirmDeleteLocation}
										colorScheme="secondary"
										isLoading={isDeletingLocation}
										loadingText="Deleting..."
									>
										Delete
									</Button>
								</Flex>
							}
							size="md"
							stickyFooter
						>
							<Flex direction="column" gap={4} p={2}>
								<Text>
									Are you sure you want to delete the location{' '}
									<Text as="span" fontWeight="bold">
										&quot;{locationToDelete?.name}&quot;
									</Text>
									?
								</Text>
							</Flex>
						</CustomModal>
					</FormProvider>
				</>
			)}
		</>
	);
};

export default CompanyAccountForm;
